import { Component, input, OnDestroy, OnInit, signal } from '@angular/core';
import { QuestionnaireService } from '../../../../api/services/questionnaire.service';
import { AuthService } from '../../../../services/auth.service';
import { ComponentsModule } from '../../../../components/components.module';
import { FormsModule } from '@angular/forms';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { QuestionnaireVersionModel } from '../../../../api/models/questionnaire-version-model';
import { ToastService } from '../../../../services/toast.service';
import { CardType } from '../../../../enums/card-type';

@Component({
  selector: 'app-questionnaire-analytics',
  templateUrl: './questionnaire-analytics.component.html',
  imports: [
    ComponentsModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
  ],
})
export class QuestionnaireAnalyticsComponent implements OnInit, OnDestroy {
  protected readonly selectedVersion = input<string>('undefined');
  protected readonly questionnaireGuid = input.required<string>();

  protected readonly isSummariesPage = signal<boolean>(false);
  protected readonly questionnaireVersions = rxResource({
    params: () => ({
      questionnaireGuid: this.questionnaireGuid(),
    }),
    stream: ({ params }) =>
      this.questionnaireService.questionnaireVersionsGet(
        { questionnaireGuid: params.questionnaireGuid },
        this.authService.getAuthorisationHeaders(),
      ),
  });

  protected readonly questionnaireTitle = signal<string>('');

  private destroy$ = new Subject<void>();

  constructor(
    private questionnaireService: QuestionnaireService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
  ) {
    toObservable(this.questionnaireVersions.value).subscribe((value) => {
      void this.updateSummariesFlag(value);
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.updateSummariesFlag());

    this.questionnaireService
      .questionnairesGet(this.authService.getAuthorisationHeaders(), {
        includeDetailsForm: true,
        includeMainSurveysOnly: false,
      })
      .subscribe((questionnaires) => {
        const selected = questionnaires.find(
          (q) => q.guid === this.questionnaireGuid(),
        );
        if (selected) {
          this.questionnaireTitle.set(selected.name);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected selectVersion(value: number | string | undefined) {
    void this.router.navigate([], {
      queryParams: { selectedVersion: value ?? 'undefined' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async updateSummariesFlag(versions?: QuestionnaireVersionModel[]) {
    this.isSummariesPage.set(
      this.activatedRoute.firstChild?.snapshot.url[0]?.path === 'summaries',
    );

    const selectedVersion = this.selectedVersion();
    const selectedVersionIsAll =
      selectedVersion === '' ||
      selectedVersion === 'undefined' ||
      selectedVersion === undefined;

    if (selectedVersionIsAll && this.isSummariesPage()) {
      this.toastService.info(
        'You must choose a specific version for summaries, the latest version was selected',
      );
      this.selectVersion(
        (versions ?? this.questionnaireVersions.value()!)[0].versionValue,
      );
      return;
    }

    if (
      selectedVersionIsAll &&
      this.activatedRoute.snapshot.queryParams['selectedVersion'] === undefined
    ) {
      this.selectVersion('undefined');
    }
  }

  protected readonly CardType = CardType;
}
